import { format, differenceInDays } from 'date-fns';
import { formatDateWithoutHours } from '@/core/common/helpers/utils';
import { formatAndCalculateReleasedatum } from '@/core/common/helpers/utils';

const formatters = {
  // Datum-Formatter
  date: value => formatDateWithoutHours(value),

  // Preis-Formatter (ersetzt Punkt durch Komma und fügt € hinzu)
  price: value => (value ? String(value).replace('.', ',') + ' €' : '-'),

  // Transferszenario (erste 2 Zeichen)
  transferszenario: value => value?.substring(0, 2),

  // Reisekürzel (erste 6 Zeichen)
  reisekuerzel: value => value?.substring(0, 6),

  // Tage
  tage: value => (value ? value + ' Tage' : '-'),

  // Uhrzeit (erste 5 Zeichen + "Uhr")
  time: value => (value ? value.substring(0, 5) + ' Uhr' : '-'),

  // null formatter
  nullsafe: value => value ?? '-',

  // Releasedatum mit Tagen bis Release
  releaseDaysAndDate: value => {
    if (value) {
      const releasedate = new Date(value);
      const tageBisRelease = differenceInDays(releasedate, new Date());
      const formattedReleasedate = format(releasedate, 'dd.MM.yyyy');
      return `${tageBisRelease} (${formattedReleasedate})`;
    }
    return '';
  },

  // Releasefristen (mehrere mit Datum)
  releasefristen: (value, key, item) => {
    if (item.metadata?.releasefrist?.length > 0) {
      return item.metadata.releasefrist
        .map(releasefrist => {
          const releasedatum = formatAndCalculateReleasedatum(item.startdatum, releasefrist);
          return `${releasefrist} (${releasedatum})`;
        })
        .join(' / ');
    }
    return '-';
  },

  // Releasefristen mit Bold für die Frist, die dem Releasedatum entspricht
  releasefristenMitBold: (value, key, item) => {
    if (item.metadata?.releasefrist?.length > 0) {
      const releasedatumFormatted = item.releasedatum ? formatDateWithoutHours(item.releasedatum) : null;
      return item.metadata.releasefrist
        .map(releasefrist => {
          const releasedatum = formatAndCalculateReleasedatum(item.startdatum, releasefrist);
          if (item.metadata?.releasefrist?.length > 1 && releasedatum === releasedatumFormatted) {
            return `<b>${releasefrist} (${releasedatum})</b>`;
          }
          return `${releasefrist} (${releasedatum})`;
        })
        .join(' / ');
    }
    return '-';
  },

  // Berechnetes OptionsfristDatum
  optionsfristMitDatum: (value, key, item) => {
    if (item.flugeinkauf?.optionsfrist != null) {
      const optionsdatum = formatAndCalculateReleasedatum(item.startdatum, item.flugeinkauf.optionsfrist);
      return `${optionsdatum}`;
    }
    return '-';
  },
};

// Ermittelt die Saison-Metadaten der Reise, die zum Abreisedatum des Reisetermins gilt
const getGueltigeSaisonMetadata = (metadataHistory, abreisedatum) => {
  if (!metadataHistory?.length) return null;
  const sorted = [...metadataHistory].sort((a, b) => new Date(b.gueltigAb) - new Date(a.gueltigAb));
  if (!abreisedatum) return sorted[0];
  const datum = new Date(abreisedatum);
  return sorted.find(m => new Date(m.gueltigAb) <= datum) ?? sorted[sorted.length - 1];
};

// Helper-Funktion zum Erstellen von Feldern mit Defaults
const field = (key, label, options = {}) => ({
  tdClass: 'align-middle',
  key,
  label,
  ...options,
});

const commonFields = {
  reiseterminkuerzel: field('reiseterminkuerzel', 'Reiseterminkürzel'),
  reiseterminkuerzelShort: field('reiseterminkuerzel', 'Reisekürzel', {
    formatter: formatters.reisekuerzel,
  }),
  reiseReisekuerzel: field('reise.reisekuerzel', 'Reisekürzel'),
  actionsRTDetailUndFlugplanung: { key: 'actionsRTDetailUndFlugplanung', label: 'Links' },
  countryManager: field('countryManagers', 'CM', {
    formatter: value => value?.map(cm => cm.countryManagerKuerzel).join(', ') ?? '-',
  }),
  // CM aus den Reisestammdaten (Saison der Reise)
  countryManagerReisestammdaten: field('reise.fullMetadataHistory', 'CM', {
    formatter: (value, key, item) => {
      const saison = getGueltigeSaisonMetadata(item.reise?.fullMetadataHistory, item.abreisedatum);
      return saison?.countryManagers?.map(cm => cm.countryManagerKuerzel).join(', ') || '-';
    },
  }),
  reisePrioritaet: field('reise.prioritaet', 'Priorität'),
  state: field('state', 'Status'),

  // Datum-Felder
  startdatum: field('startdatum', 'Startdatum', { formatter: formatters.date }),
  abreisedatum: field('abreisedatum', 'Abreisedatum', { formatter: formatters.date }),
  releasedatum: field('releasedatum', 'Releasedatum', { formatter: formatters.date }),
  releasedatumMitTagen: field('releasedatum', 'Tage bis Release', {
    formatter: formatters.releaseDaysAndDate,
  }),
  unterlagenversandDatum: field('unterlagenversandDatum', 'Versand', {
    formatter: formatters.date,
  }),

  // PAX-Felder
  paxWidget: field('paxWidget', 'PAX'),

  // Status-Felder
  abgesagt: field('abgesagt', 'Abgesagt'),
  garantiert: field('garantiert', 'Garantiert'),
  garantiertShort: field('garantiert', 'Gar.'),
  vorzeitigGarantiert: field('vorzeitigGarantiert', 'Vorz. gar.'),
  stopSale: field('stopSale', 'Stop Sale', { thStyle: { whiteSpace: 'nowrap' } }),

  // Reisedauer
  reisezeitraum: field('reisezeitraum'),
  reisedauer: field('reisedauer', 'Reisedauer', {
    formatter: value => (value ? value + ' Tage' : '-'),
  }),

  // Transferszenario
  tsHin: field('metadata.transferszenarioHin', 'TS Hin', {
    formatter: formatters.transferszenario,
  }),
  tsRueck: field('metadata.transferszenarioRueck', 'TS Rück', {
    formatter: formatters.transferszenario,
  }),

  // Overnight-Felder
  overnightOutbound: field('metadata.overnightOutbound', 'OB'),
  overnightInbound: field('metadata.overnightInbound', 'IB'),

  // Hotel/Flug-Felder
  hotelstartVorLandung: field('metadata.hotelstartVorLandung', 'Hotelstart v. Landung', {
    thStyle: { whiteSpace: 'nowrap' },
  }),
  rueckflugNachHotelende: field('metadata.rueckflugNachHotelende', 'Rückflug n. Hotelende', {
    thStyle: { whiteSpace: 'nowrap' },
  }),
  ziellandZielUndAblugflughafen: field('ziellandZielUndAblugflughafen', 'Zielland Ziel-/Abfh.', {
    thStyle: { whiteSpace: 'nowrap' },
  }),
  TSHinUndRueck: field('TSHinUndRueck', 'TS Hin/Rück', {
    thStyle: { whiteSpace: 'nowrap' },
  }),

  // TH Felder
  thInlandsflug: field('thInlandsflug', 'TH Inlandsflug', { formatter: formatters.price }),
  thGesamt: field('thGesamt', 'TH Gesamt', { formatter: formatters.price }),
  thLangstrecke: field('thLangstrecke', 'TH Langstrecke', { formatter: formatters.price }),

  // Releaseprozess-Felder
  releasefrist: field('releasefrist', 'Releasefristen', {
    formatter: formatters.releasefristen,
  }),
  releasefristMitBold: field('releasefrist', 'Releasefristen', {
    formatter: formatters.releasefristenMitBold,
  }),
  releasefristMitTextNowrap: field('releasefrist', 'Releasefristen', {
    tdClass: 'align-middle text-nowrap',
    thStyle: { whiteSpace: 'nowrap' },
    formatter: formatters.releasefristen,
  }),

  // Releaseprozess Icons
  teilnehmerdatenStatus: field('releaseprozessStatus.teilnehmerdatenStatus', 'TD'),
  reiseleistungListenversandStatus: field('releaseprozessStatus.reiseleistungListenversandStatus', 'GL'),
  fluegeStatus: field('releaseprozessStatus.fluegeStatus', 'F'),
  transferplanungStatus: field('releaseprozessStatus.transferplanungStatus', 'TP'),
  reiseunterlagenGeprueft: field('hotelsUndProgrammGeprueft', 'RTE'),
  transferplanungGeprueft: field('transferplanungGeprueft', 'FTP'),

  // Weitere Felder
  zimmerkontingenteWidget: field('zimmerkontingenteWidget', 'Zimmerkontingente'),
  nochForecastPax: field('nochForecastPax', 'Noch FC PAX'),
  paxBisMax: field('paxBisMax', 'PAX bis Max'),
  flugverfuegbarkeiten: field('flugverfuegbarkeiten', 'Flugverfügbarkeiten'),
  isKritischeDestination: field('reise.isKritischeDestination', 'Krit. Dest.'),
  commentCount: field('commentCount', 'Kommentare'),
  aufgaben: field('aufgaben', 'Aufgaben'),
};

// Export Fields

export const zvcVerlinkungenFields = [
  commonFields.reiseterminkuerzelShort,
  commonFields.reisePrioritaet,
  commonFields.startdatum,
  commonFields.garantiertShort,
  commonFields.releasedatumMitTagen,
  commonFields.zimmerkontingenteWidget,
  commonFields.paxWidget,
  commonFields.nochForecastPax,
  commonFields.paxBisMax,
  commonFields.flugverfuegbarkeiten,
  commonFields.aufgaben,
];

export const fomVerlinkungenFields = [
  commonFields.reiseReisekuerzel,
  commonFields.actionsRTDetailUndFlugplanung,
  commonFields.reisePrioritaet,
  commonFields.abreisedatum,
  commonFields.startdatum,
  commonFields.paxWidget,
  commonFields.tsHin,
  commonFields.tsRueck,
  commonFields.thLangstrecke,
  commonFields.thInlandsflug,
  commonFields.thGesamt,
  commonFields.aufgaben,
];

export const fomUnverbindlichesFlugangebotVerlinkungenFields = [
  commonFields.reiseReisekuerzel,
  commonFields.actionsRTDetailUndFlugplanung,
  commonFields.reisePrioritaet,
  commonFields.abreisedatum,
  commonFields.startdatum,
  commonFields.paxWidget,
  commonFields.garantiertShort,
  commonFields.vorzeitigGarantiert,
  commonFields.tsHin,
  commonFields.tsRueck,
  commonFields.thLangstrecke,
  commonFields.thInlandsflug,
  commonFields.thGesamt,
  commonFields.aufgaben,
];

export const fieldsVorabpruefungTicketBeiGruppe = [
  commonFields.reiseReisekuerzel,
  commonFields.actionsRTDetailUndFlugplanung,
  commonFields.reisePrioritaet,
  commonFields.abreisedatum,
  commonFields.startdatum,
  commonFields.paxWidget,
  commonFields.tsHin,
  commonFields.tsRueck,
  commonFields.thLangstrecke,
  commonFields.thInlandsflug,
  commonFields.thGesamt,
  field('flugeinkauf.optionsfrist', 'Optionsfrist', {
    formatter: formatters.optionsfristMitDatum,
  }),
  commonFields.aufgaben,
];

export const releaseprozessVerlinkungenFields = [
  commonFields.reiseterminkuerzelShort,
  commonFields.countryManager,
  commonFields.state,
  commonFields.startdatum,
  commonFields.releasefristMitBold,
  commonFields.unterlagenversandDatum,
  commonFields.teilnehmerdatenStatus,
  commonFields.reiseleistungListenversandStatus,
  commonFields.fluegeStatus,
  commonFields.transferplanungStatus,
  commonFields.reiseunterlagenGeprueft,
  commonFields.transferplanungGeprueft,
  commonFields.garantiertShort,
  commonFields.paxWidget,
  commonFields.isKritischeDestination,
  commonFields.commentCount,
  commonFields.aufgaben,
];

export const basicVerlinkungenFields = [
  commonFields.reiseterminkuerzel,
  commonFields.actionsRTDetailUndFlugplanung,
  commonFields.countryManager,
  commonFields.startdatum,
  commonFields.reisezeitraum,
  commonFields.reisedauer,
  commonFields.releasedatum,
  commonFields.paxWidget,
  commonFields.abgesagt,
  commonFields.garantiertShort,
  commonFields.vorzeitigGarantiert,
  commonFields.stopSale,
  commonFields.overnightOutbound,
  commonFields.overnightInbound,
  commonFields.hotelstartVorLandung,
  commonFields.rueckflugNachHotelende,
  commonFields.ziellandZielUndAblugflughafen,
  commonFields.TSHinUndRueck,
  commonFields.aufgaben,
];

export const TicketingAnfrageVerlinkungenFields = [
  commonFields.reiseterminkuerzel,
  commonFields.actionsRTDetailUndFlugplanung,
  commonFields.startdatum,
  commonFields.reisezeitraum,
  commonFields.reisedauer,
  commonFields.releasedatum,
  commonFields.paxWidget,
  commonFields.abgesagt,
  commonFields.garantiertShort,
  commonFields.vorzeitigGarantiert,
  commonFields.stopSale,
  commonFields.overnightOutbound,
  commonFields.overnightInbound,
  commonFields.hotelstartVorLandung,
  commonFields.rueckflugNachHotelende,
  commonFields.ziellandZielUndAblugflughafen,
  commonFields.TSHinUndRueck,
  field('flugeinkaufErsteGruppeFlughafen', 'GRP FH (PNR)'),
  field('flugeinkaufErsteGruppeGesamtpreis', 'GRP Gesamtpreis'),
  commonFields.aufgaben,
];

export const fieldsZTVTGPruefen = [
  commonFields.reiseterminkuerzel,
  commonFields.actionsRTDetailUndFlugplanung,
  commonFields.startdatum,
  commonFields.reisezeitraum,
  commonFields.reisedauer,
  commonFields.releasefristMitTextNowrap,
  commonFields.paxWidget,
  commonFields.abgesagt,
  commonFields.garantiertShort,
  commonFields.vorzeitigGarantiert,
  commonFields.stopSale,
  commonFields.overnightOutbound,
  commonFields.overnightInbound,
  commonFields.hotelstartVorLandung,
  commonFields.rueckflugNachHotelende,
  commonFields.ziellandZielUndAblugflughafen,
  commonFields.TSHinUndRueck,
  commonFields.aufgaben,
];

export const fieldsGruppeneinkaufPruefen = [
  commonFields.reiseterminkuerzel,
  field('metadata.transferszenarioHin', 'Transferszenario Hin', {
    formatter: value => (value ? value.substring(0, 2) : '-'),
  }),
  field('metadata.transferszenarioRueck', 'Transferszenario Rück', {
    formatter: value => (value ? value.substring(0, 2) : '-'),
  }),
  commonFields.actionsRTDetailUndFlugplanung,
  commonFields.aufgaben,
];

export const fieldsAnlageHotelstamm = [
  commonFields.reiseterminkuerzel,
  commonFields.countryManagerReisestammdaten,
  field('metadata.hotelnaechte', 'Hotelnächte', { formatter: formatters.nullsafe }),
  field('metadata.minPax', 'Min Pax', { formatter: formatters.nullsafe }),
  field('metadata.maxPax', 'Max Pax', { formatter: formatters.nullsafe }),
  field('metadata.releasefrist', 'Releasefrist', { formatter: formatters.nullsafe }),
  field('metadata.hotelstartVorLandung', 'Hotelstart v. Landung'),
  field('planZimmerkontingenteWidget', 'Zimmerkontingente Plan'),
  commonFields.aufgaben,
];

export const fieldsTerminplanungAufgaben = [
  commonFields.reiseterminkuerzel,
  commonFields.state,
  commonFields.startdatum,
  field('reisezeitraum', 'Reisezeitraum'),
  commonFields.reisedauer,
  field('verkehrstagHin', 'VT Hin', { formatter: formatters.nullsafe }),
  commonFields.ziellandZielUndAblugflughafen,
  field('planZimmerkontingenteWidget', 'Zimmerkontingente Plan'),
];

export const fieldsAnlageFlugstamm = [
  commonFields.reiseterminkuerzel,
  field('actionsRTDetail', 'Links'),
  commonFields.countryManager,
  field('abreisedatum', 'Abreise', { formatter: formatters.date }),
  field('hotelendUndRueckflugdatum', 'Rückflug - datum', { formatter: formatters.date }),
  field('metadata.transferszenarioHin', 'TF Hin', { formatter: formatters.transferszenario }),
  field('metadata.transferszenarioRueck', 'TF Rück', { formatter: formatters.transferszenario }),
  field('metadata.skrFlugangebot', 'SKR Flugangebot', {
    formatter: value => (value ? value + ' €' : '-'),
  }),
  field('ziellandZielUndAblugflughafen', 'ZL Zielflughafen /Abflughafen', {
    formatter: formatters.nullsafe,
  }),
  field('metadata.overnightOutbound', 'OB', { formatter: formatters.nullsafe }),
  field('metadata.overnightInbound', 'IB', { formatter: formatters.nullsafe }),
  field('metadata.hotelstartVorLandung', 'Hotelstart v. Landung', { formatter: formatters.nullsafe }),
  field('metadata.rueckflugNachHotelende', 'Rückflug n. Hotelende', { formatter: formatters.nullsafe }),
  field('metadata.fruehesteAnkunft', 'Früh. Ankuft Hin', { formatter: formatters.time }),
  field('metadata.spaetesteAnkunft', 'Spät. Ankuft Hin', { formatter: formatters.time }),
  field('metadata.fruehesterAbflug', 'Früh. Abflug Rück', { formatter: formatters.time }),
  field('metadata.spaetesterAbflug', 'Spät. Abflug Rück', { formatter: formatters.time }),
  field('metadata.vorverlaengerung', 'Vor-verlängerung', { formatter: formatters.nullsafe }),
  field('metadata.vkVornachtDzProPerson', 'VK kalk. Vornacht DZ p.P.', { formatter: formatters.price }),
  commonFields.thLangstrecke,
  field('inlandsflugEinkauf', 'INFL: Einkauf'),
  commonFields.aufgaben,
];

export const fieldsOptionsfristErreicht = [
  commonFields.reiseterminkuerzel,
  commonFields.actionsRTDetailUndFlugplanung,
  commonFields.state,
  field('flugeinkaufStatusgruppe', 'Statusgruppe'),
  field('flugeinkaufEinkaufsart', 'Einkaufsart'),
  commonFields.paxWidget,
  field('flugeinkaufGruppeFlughafen', 'GRP FH (PNR)'),
  commonFields.thLangstrecke,
  commonFields.thGesamt,
  field('flugeinkauf.optionsfrist', 'Optionsfrist', { formatter: formatters.tage }),
  commonFields.aufgaben,
];

export const fieldsStopsaleSetzen = [
  commonFields.reiseterminkuerzel,
  commonFields.countryManager,
  commonFields.abreisedatum,
  commonFields.startdatum,
  commonFields.paxWidget,
  commonFields.abgesagt,
  commonFields.garantiert,
  commonFields.stopSale,
  commonFields.hotelstartVorLandung,
  commonFields.rueckflugNachHotelende,
  commonFields.ziellandZielUndAblugflughafen,
  commonFields.zimmerkontingenteWidget,
  commonFields.aufgaben,
];

export const teilnehmerdokumenteVerlinkungenFields = [
  commonFields.reiseterminkuerzelShort,
  commonFields.state,
  commonFields.startdatum,
  commonFields.releasedatum,
  commonFields.releasefrist,
  commonFields.unterlagenversandDatum,
  commonFields.teilnehmerdatenStatus,
  commonFields.reiseleistungListenversandStatus,
  commonFields.fluegeStatus,
  commonFields.transferplanungStatus,
  commonFields.reiseunterlagenGeprueft,
  commonFields.transferplanungGeprueft,
  commonFields.garantiertShort,
  commonFields.paxWidget,
  commonFields.isKritischeDestination,
  field('benoetigteDokumente', 'Benötigte Dokumente'),
  commonFields.commentCount,
  commonFields.aufgaben,
];

export const kurzesTktlFields = [
  commonFields.reiseReisekuerzel,
  commonFields.actionsRTDetailUndFlugplanung,
  commonFields.reisePrioritaet,
  commonFields.abreisedatum,
  commonFields.startdatum,
  commonFields.paxWidget,
  field('flugeinkauf.einkaufsart', 'Einkaufsart'),
  commonFields.tsHin,
  commonFields.tsRueck,
  commonFields.thLangstrecke,
  commonFields.thInlandsflug,
  commonFields.thGesamt,
  commonFields.aufgaben,
];

export const minMaxPaxPruefenFields = [
  commonFields.reiseterminkuerzel,
  commonFields.countryManager,
  commonFields.actionsRTDetailUndFlugplanung,
  commonFields.state,
  commonFields.startdatum,
  field('metadata.minPax', 'Min Pax Manager'),
  field('pax.min', 'Min Pax Blank'),
  field('metadata.maxPax', 'Max Pax Manager'),
  field('pax.max', 'Max Pax Blank'),
  commonFields.abgesagt,
  commonFields.garantiertShort,
  commonFields.stopSale,
  commonFields.aufgaben,
];

export const repruAufgabenFields = [
  // {... } wird verwendet, damit man den commonFields.XYZ ein thStyle anhängen kann
  { ...commonFields.reiseterminkuerzel, thStyle: { width: '14.28%' } },
  { ...commonFields.startdatum, thStyle: { width: '14.28%' } },
  { ...commonFields.reisezeitraum, thStyle: { width: '14.28%' } },
  { ...commonFields.reisedauer, thStyle: { width: '14.28%' } },
  { ...commonFields.releasedatum, thStyle: { width: '14.28%' } },
  { ...commonFields.paxWidget, thStyle: { width: '14.28%' } },
  { ...commonFields.aufgaben, thStyle: { width: '14.28%' } },
];
